// Gantt / Task-scheduler visual — thin React host around insight-bi's vanilla-JS
// TaskScheduler class (vendored verbatim under ../vendor/taskScheduler.js with a
// window export appended). This is the same approach insight-bi uses: it embeds
// the plain-JS scheduler in an Angular template; we embed it in React. The class
// reads jsonData [schemaRow, ...rows] + visualLayout.ColumnGroups (StandardName
// rows / columns / values) and paints a timeline table with draggable bars.
//
// twasta canonicalises data keys to the ColumnGroup NAME casing, but the
// scheduler reads resource columns lower-cased — so we hand it rows augmented
// with lower-cased key aliases (and a deep clone, since the scheduler mutates
// rows in place).

import { useEffect, useRef, useState } from 'react'
import { ensureVendor } from './loader'
import type { NativeRendererProps } from './nativeRenderers'
import type { VisualJsonData } from './types'

const GANTT_STYLE_ID = 'twasta-gantt-css'
// Colours are the page / host tokens with the original light values as
// fallbacks, so the chart follows a dark host (see surfaceTheme.ts).
const GANTT_CSS = `
.twasta-gantt { width: 100%; height: 100%; font-size: 12px; }
.twasta-gantt .task-scheduler-wrapper { overflow: auto; max-height: 100%; }
.twasta-gantt .task-scheduler-table { border-collapse: collapse; width: 100%; }
.twasta-gantt .task-scheduler-table th, .twasta-gantt .task-scheduler-table td { border: 1px solid var(--pg-border, var(--color-border, #ddd)); padding: 4px 6px; text-align: center; position: relative; }
.twasta-gantt .task-scheduler-table th { background-color: var(--pg-surface, var(--color-surface-2, #f2f2f2)); font-weight: 600; }
.twasta-gantt .resource-column { background: var(--color-bg-secondary, #fafafa); text-align: left !important; white-space: nowrap; }
.twasta-gantt .timeline-header { text-align: center; min-width: 28px; }
.twasta-gantt .expand-collapse-icon { font-size: 12px; cursor: pointer; user-select: none; margin-right: 5px; }
.twasta-gantt .timeline-cell-container, .twasta-gantt .timeline-container { position: relative; height: 100%; pointer-events: auto; }
.twasta-gantt .task-bar { position: absolute; background-color: #3b82f6; color: #fff; border: 1px solid #1d4ed8; box-sizing: border-box; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; cursor: move; user-select: none; height: 20px; border-radius: 3px; font-size: 11px; padding: 0 4px; line-height: 20px; z-index: 1; }
.twasta-gantt .task-bar.dragging { opacity: 0.5; z-index: 1000; }
.twasta-gantt .tasks-available-line { background-color: #94a3b8 !important; }
.twasta-gantt .resize-handle { position: absolute; width: 5px; height: 100%; top: 0; background-color: rgba(0,0,0,0.4); cursor: ew-resize; opacity: 0.5; }
.twasta-gantt .resize-handle.left { left: 0; } .twasta-gantt .resize-handle.right { right: 0; }
.twasta-gantt .controls-container { display: flex; align-items: center; gap: 16px; padding: 6px 10px; border-top: 1px solid var(--pg-border, var(--color-border, #e5e7eb)); background: var(--pg-surface, var(--color-surface-2, #f8f9fa)); }
.twasta-gantt .zoom-btn { background: var(--color-bg-secondary, #fff); color: var(--color-text, inherit); border: 1px solid var(--pg-border, var(--color-border, #ccc)); border-radius: 4px; cursor: pointer; padding: 2px 8px; }
.twasta-gantt #vertical-drop-line { position: absolute; top: 0; bottom: 0; width: 2px; background-color: red; pointer-events: none; z-index: 9999; }
`

function injectGanttCss(): void {
  if (typeof document === 'undefined' || document.getElementById(GANTT_STYLE_ID)) return
  const el = document.createElement('style')
  el.id = GANTT_STYLE_ID
  el.textContent = GANTT_CSS
  document.head.appendChild(el)
}

// Deep-clone rows and add lower-cased key aliases so the scheduler's lower-cased
// resource-column lookups resolve against twasta's NAME-cased data keys.
function augmentData(jsonData: VisualJsonData): VisualJsonData {
  const schema = jsonData[0]
  const rows = jsonData.slice(1) as Array<Record<string, unknown>>
  const out = rows.map((r) => {
    const o: Record<string, unknown> = { ...r }
    for (const k of Object.keys(r)) {
      const lk = k.toLowerCase()
      if (!(lk in o)) o[lk] = r[k]
    }
    return o
  })
  return [schema, ...out] as VisualJsonData
}

let _ganttSeq = 0

export function GanttChart({ jsonData, visualLayout }: NativeRendererProps) {
  const ref = useRef<HTMLDivElement>(null)
  const idRef = useRef<string>(`twasta-gantt-${(_ganttSeq += 1)}`)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    injectGanttCss()
    const rows = (jsonData?.slice(1) ?? []) as unknown[]
    if (!rows.length) { setErr('No data.'); return }
    setErr(null)

    ensureVendor(['TaskScheduler'])
      .then(() => {
        if (cancelled || !ref.current) return
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const TaskScheduler = (window as any).TaskScheduler
        if (typeof TaskScheduler !== 'function') {
          setErr('Task scheduler failed to load.')
          return
        }
        ref.current.innerHTML = ''
        try {
          // eslint-disable-next-line no-new
          new TaskScheduler({
            containerId: idRef.current,
            jsonData: augmentData(jsonData),
            optionsArray: (visualLayout.options as unknown[]) ?? [],
            visualLayout,
            IsEditMode: false,
          })
        } catch (e) {
          setErr(`Task scheduler error: ${e instanceof Error ? e.message : String(e)}`)
        }
      })
      .catch((e) => { if (!cancelled) setErr(String(e)) })

    return () => {
      cancelled = true
      if (ref.current) ref.current.innerHTML = ''
    }
  }, [jsonData, visualLayout])

  if (err) return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, var(--color-text-secondary, #888))', textAlign: 'center' }}>{err}</div>
  return <div ref={ref} id={idRef.current} className="twasta-gantt" style={{ width: '100%', height: '100%', minHeight: 320, overflow: 'auto' }} />
}

export default GanttChart
